import { useMemo, useState } from "react";
import { formatMoney } from "../../lib/format";
import { TokenIcon } from "../board/TokenIcon";
import { downsample, linePath, linearScale, niceScale, tickValues } from "./chartMath";
import { ChartFrame, ChartTooltip } from "./ChartFrame";
import { describeWealth, turnsOf, worthAt } from "./resultLogic";
import type { WealthSeries } from "./resultLogic";
import { useElementWidth } from "./useElementWidth";

const MARGIN = { top: 14, right: 14, bottom: 30, left: 58 };
const TABLE_ROWS = 15;

const number = (n: number) => n.toLocaleString("de-DE");

/** Net worth of every player over the course of the game: one line per player in the player's color. */
export function WealthChart({ series }: { series: WealthSeries[] }) {
  const [wrapRef, width] = useElementWidth<HTMLDivElement>();
  const [cursor, setCursor] = useState<number | null>(null);
  const [focus, setFocus] = useState<string | null>(null);

  const turns = useMemo(() => turnsOf(series), [series]);
  const maxTurn = turns[turns.length - 1] ?? 0;
  const maxWorth = Math.max(0, ...series.flatMap((s) => s.points.map((p) => p.worth)));
  const yScale = niceScale(maxWorth, 4);
  const height = Math.round(Math.min(340, Math.max(220, width * 0.5)));
  const baseline = height - MARGIN.bottom;
  const x = linearScale(0, maxTurn, MARGIN.left, width - MARGIN.right);
  const y = linearScale(0, yScale.max, baseline, MARGIN.top);
  const xTicks = tickValues(maxTurn, width < 480 ? 5 : 7);

  const paths = series.map((s) => ({
    s,
    d: linePath(s.points.map((p) => ({ x: x(p.turn), y: y(p.worth) }))),
    end: s.points[s.points.length - 1],
  }));

  const onMove = (clientX: number, svg: SVGSVGElement) => {
    const rect = svg.getBoundingClientRect();
    const px = clientX - rect.left;
    let best = turns[0] ?? 0;
    let bestDist = Infinity;
    for (const t of turns) {
      const d = Math.abs(x(t) - px);
      if (d < bestDist) {
        best = t;
        bestDist = d;
      }
    }
    setCursor(best);
  };

  const cursorRows =
    cursor === null
      ? []
      : series
          .map((s) => ({ s, worth: worthAt(s, cursor) }))
          .sort((a, b) => (b.worth ?? -1) - (a.worth ?? -1));

  const tableTurns = downsample(turns, TABLE_ROWS);

  const legend = (
    <ul className="flex flex-wrap gap-x-1 gap-y-0.5" aria-label="Legende">
      {series.map((s) => (
        <li key={s.id}>
          <button
            type="button"
            aria-pressed={focus === s.id}
            onClick={() => setFocus(focus === s.id ? null : s.id)}
            onPointerEnter={() => setFocus(s.id)}
            onPointerLeave={() => setFocus(null)}
            className="flex min-h-11 items-center gap-2 rounded-lg px-2 text-sm text-ink hover:bg-surface-2 aria-pressed:bg-surface-2"
          >
            <span aria-hidden="true" className="inline-block h-[3px] w-5 rounded-full" style={{ background: s.color }} />
            <TokenIcon token={s.token} color={s.colorId} size={18} />
            <span>{s.name}</span>
            {s.eliminated && <span className="text-ink-2">(ausgeschieden)</span>}
          </button>
        </li>
      ))}
    </ul>
  );

  return (
    <ChartFrame
      id="wealth"
      title="Vermögensverlauf"
      subtitle="Bargeld, Kaufpreise und Gebäudewerte abzüglich Hypotheken, je Zug. Ein Klick auf einen Namen hebt die Linie hervor."
      legend={legend}
      table={{
        caption: "Vermögen der Spieler zu ausgewählten Zügen",
        head: ["Zug", ...series.map((s) => s.name)],
        rows: tableTurns.map((t) => [
          t,
          ...series.map((s) => {
            const w = worthAt(s, t);
            return w === null ? "ausgeschieden" : formatMoney(w);
          }),
        ]),
      }}
    >
      <div ref={wrapRef} className="relative">
        <svg
          role="img"
          aria-label={describeWealth(series)}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          className="block max-w-full touch-pan-y"
          onPointerMove={(e) => onMove(e.clientX, e.currentTarget)}
          onPointerDown={(e) => onMove(e.clientX, e.currentTarget)}
          onPointerLeave={() => setCursor(null)}
        >
          {yScale.ticks.map((t) => (
            <g key={t}>
              <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t)} y2={y(t)} className="stroke-line" strokeWidth={1} />
              <text x={MARGIN.left - 8} y={y(t)} dy="0.32em" textAnchor="end" className="fill-ink-2 text-[11px] tabular-nums">
                {number(t)}
              </text>
            </g>
          ))}
          <line x1={MARGIN.left} x2={width - MARGIN.right} y1={baseline} y2={baseline} className="stroke-ink-2" strokeWidth={1} />
          {xTicks.map((t) => (
            <text key={t} x={x(t)} y={height - 9} textAnchor={t === 0 ? "start" : t === maxTurn ? "end" : "middle"} className="fill-ink-2 text-[11px] tabular-nums">
              {t === 0 ? "Zug 0" : t}
            </text>
          ))}

          {paths.map(({ s, d }) => (
            <path
              key={s.id}
              d={d}
              fill="none"
              stroke={s.color}
              strokeWidth={focus === s.id ? 3.5 : 2}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={focus && focus !== s.id ? 0.2 : 1}
            />
          ))}
          {paths.map(({ s, end }) =>
            end ? (
              <circle
                key={s.id}
                cx={x(end.turn)}
                cy={y(end.worth)}
                r={4}
                fill={s.color}
                className="stroke-surface"
                strokeWidth={2}
                opacity={focus && focus !== s.id ? 0.2 : 1}
              />
            ) : null,
          )}

          {cursor !== null && (
            <g>
              <line x1={x(cursor)} x2={x(cursor)} y1={MARGIN.top} y2={baseline} className="stroke-ink-2" strokeWidth={1} />
              {cursorRows.map(({ s, worth }) =>
                worth === null ? null : (
                  <circle key={s.id} cx={x(cursor)} cy={y(worth)} r={4} fill={s.color} className="stroke-surface" strokeWidth={2} />
                ),
              )}
            </g>
          )}
        </svg>
        {cursor !== null && (
          <ChartTooltip x={x(cursor)} width={width}>
            <p className="mb-1 font-semibold">Zug {cursor}</p>
            <ul className="space-y-0.5">
              {cursorRows.map(({ s, worth }) => (
                <li key={s.id} className="flex items-center gap-2 tabular-nums">
                  <span aria-hidden="true" className="inline-block size-2.5 shrink-0 rounded-full" style={{ background: s.color }} />
                  <span className="max-w-[9rem] truncate">{s.name}</span>
                  <span className="ml-auto pl-3 text-ink-2">{worth === null ? "ausgeschieden" : formatMoney(worth)}</span>
                </li>
              ))}
            </ul>
          </ChartTooltip>
        )}
      </div>
    </ChartFrame>
  );
}
